import { Link } from "react-router-dom";
import {
  LayoutDashboard,
  Gift,
  Users,
  Settings,
  CreditCard,
  BarChart3,
  Package,
  Wallet,
  User,
  Receipt,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { NavDocuments } from "./NavDocuments";
import styles from "./AppSidebar.module.scss";
import { useTranslation } from "react-i18next";

interface BaseMenuItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface RegularMenuItem extends BaseMenuItem {
  type: "item";
  url: string;
}

interface CollapsibleMenuItem extends BaseMenuItem {
  type: "group";
  items: {
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

type MenuItem = RegularMenuItem | CollapsibleMenuItem;

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  userRole: "user" | "super_admin";
}

// Admin menu items - có thêm page cho admin thì thêm nav ở đây
const adminMenuItems: MenuItem[] = [
  {
    type: "item",
    title: "User Interface",
    url: "/",
    icon: LayoutDashboard,
  },
  {
    type: "item",
    title: "Dashboard",
    url: "/admin/dashboard",
    icon: LayoutDashboard,
  },
  {
    type: "group",
    title: "Users",
    icon: Users,
    items: [
      {
        title: "User Management",
        url: "/admin/users",
        icon: Users,
      },
      {
        title: "Create Batch Accounts",
        url: "/admin/users/create-batch",
        icon: Users,
      },
    ],
  },
  {
    type: "item",
    title: "Analytics",
    url: "/admin/analytics",
    icon: BarChart3,
  },
  {
    type: "item",
    title: "Users",
    url: "/admin/users",
    icon: Users,
  },
  {
    type: "item",
    title: "Plans",
    url: "/admin/plans",
    icon: Package,
  },
  {
    type: "item",
    title: "Transactions",
    url: "/admin/transactions",
    icon: Receipt,
  },
  {
    type: "item",
    title: "Investment Transactions",
    url: "/admin/investment",
    icon: CreditCard,
  },
  {
    type: "item",
    title: "Rewards",
    url: "/admin/rewards",
    icon: Gift,
  },
  {
    type: "item",
    title: "Commissions",
    url: "/admin/commissions",
    icon: Gift,
  },
  {
    type: "group",
    title: "Setting",
    icon: Settings,
    items: [
      {
        title: "Revenue Management",
        url: "/admin/settings/revenue",
        icon: CreditCard,
      },
      {
        title: "Settle Daily",
        url: "/admin/settings/settle-daily",
        icon: BarChart3,
      },
    ],
  },
  {
    type: "item",
    title: "Profile",
    url: "/admin/profile",
    icon: User,
  },
];

export function AppSidebar({ userRole, ...props }: AppSidebarProps) {
  const { state } = useSidebar();
  const { t } = useTranslation();

  // User menu items, có thêm page nào thì thêm ở đây
  const userMenuItems: MenuItem[] = [
    {
      type: "item",
      title: t("sidebar.home"),
      url: "/home",
      icon: LayoutDashboard,
    },
    {
      type: "item",
      title: t("sidebar.plan_packages"),
      url: "/plans",
      icon: Package,
    },
    {
      type: "item",
      title: t("sidebar.members"),
      url: "/members",
      icon: Users,
    },
    {
      type: "item",
      title: t("sidebar.transaction_history"),
      url: "/transactions",
      icon: Receipt,
    },
    {
      type: "item",
      title: t("sidebar.commissions"),
      url: "/settings/billing",
      icon: CreditCard,
    },
    {
      type: "item",
      title: t("sidebar.referrals"),
      url: "/settings/referrals",
      icon: Gift,
    },
    {
      type: "item",
      title: t("sidebar.my_investments"),
      url: "/profile/investment",
      icon: Wallet,
    },
    {
      type: "item",
      title: t("sidebar.settings"),
      url: "/profile",
      icon: Settings,
    },
  ];

  const renderHeader = () => {
    if (userRole === "super_admin") {
      return (
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <Link to="/admin/dashboard" className={styles.headerLink}>
                <div className={styles.headerContent}>
                  <div className={styles.logoWrapper}>
                    <img src="/images/logo/logo.png" alt="" />
                  </div>
                  {state === "expanded" && (
                    <div className={styles.headerText}>
                      <span className={styles.brandName}>BeQ Rewards</span>
                      <span className={styles.adminBadge}>Admin</span>
                    </div>
                  )}
                </div>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
      );
    }

    // USER header
    return (
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/home" className={styles.headerLink}>
              <div className={styles.headerContent}>
                <img src="/images/logo/logoweb.webp" alt="BeQ Rewards" className="w-10 h-full" />
                {state === "expanded" && (
                  <span className={styles.brandName}>BeQ Rewards</span>
                )}
              </div>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
    );
  };

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        styles.appSidebar,
        state === "collapsed" ? styles.collapsed : styles.expanded,
      )}
      {...props}
    >
      {renderHeader()}

      <SidebarContent>
        <NavDocuments
          items={userRole === "super_admin" ? adminMenuItems : userMenuItems}
        />
      </SidebarContent>
    </Sidebar>
  );
}
